"use client";

import React from "react";
import { UnifiedModal } from "@/components/modal";
import { Button } from "@/components/ui/button";
import { AlertCircle, Search, Lock } from "lucide-react";
import Link from "next/link";

interface SignupRequiredModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Signup Required Modal
 *
 * Shown when users need to sign up to access features.
 * Migrated to use UnifiedModal for consistency.
 */
export function SignupRequiredModal({
  isOpen,
  onClose,
}: SignupRequiredModalProps) {
  return (
    <UnifiedModal
      open={isOpen}
      onOpenChange={onClose}
      title="Create a Free Account"
      description="Sign up for free to search and list your commercial properties and connect local Tradesmen, Engineers, Surveyers and brokers"
      size="md"
      showCloseButton={false}
      className="!bg-usland-white/50 sm:max-w-md"
    >
      <div className="flex flex-col gap-4 py-4">
        {/* Feature highlights */}
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary">
              <Lock className="h-3.5 w-3.5 text-primary-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium">Full Access to Listings</p>
              <p className="text-sm text-muted-foreground">
                Search thousands of commercial properties nationwide
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary">
              <AlertCircle className="h-3.5 w-3.5 text-primary-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium">Connect with Brokers</p>
              <p className="text-sm text-muted-foreground">
                Message brokers and schedule property showings
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary">
              <Search className="h-3.5 w-3.5 text-primary-foreground" />
            </div>
            <div>
              <p className="text-sm font-medium">Advanced Search Tools</p>
              <p className="text-sm text-muted-foreground">
                Save searches and get alerts for new listings
              </p>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col gap-2 border-t pt-4">
          <Button
            asChild
            size="lg"
            className="w-full bg-primary font-semibold text-primary-foreground hover:bg-primary/90"
          >
            <Link href="/auth/signup?redirectTo=/pricing">
              Create Free Account
            </Link>
          </Button>
          <Button
            asChild
            variant="outline"
            size="lg"
            className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground"
          >
            <Link href="/auth/signin?redirectTo=/pricing">Sign In</Link>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="w-full text-muted-foreground hover:bg-muted"
          >
            Maybe Later
          </Button>
        </div>
      </div>
    </UnifiedModal>
  );
}
